import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { CarItemCard } from '@components/Home/CarItemCard';
import { NoddiCircularLoaderWithTrans } from '@components/Loading/NoddiCircularLoaderWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { CarFleetViewRecord } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBasicCard, NoddiButton, NoddiEmptyPage, TablePagination, usePagination } from 'noddi-ui-web';
import { useStartNewBooking } from '../../../hooks/useStartNewBooking';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../../utils/commonTranslations';

const PAGE_SIZE = 5;

export const OrganizationMemberCars = ({ userGroupId }: { userGroupId: number }) => {
  const { currentBrand } = useCurrentBrand();
  const texts = getCommonTranslations();
  const { pagination, setPagination } = usePagination({ initialPageSize: PAGE_SIZE });
  const startBooking = useStartBookingForCar();
  const {
    data: cars,
    error,
    isFetching
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedFleetViewCars,
    input: {
      userGroupIds: [userGroupId],
      brandDomains: [currentBrand],
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize
    },
    queryConfig: { placeholderData: keepPreviousData }
  });

  return (
    <section className='flex flex-col gap-3'>
      <h2 className='text-xl font-bold text-primary md:text-3xl'>
        <Trans>My cars</Trans>
      </h2>
      {/* A failed refresh keeps the cars already on screen. */}
      {error && <ApiErrorMessageWithTrans error={error} />}
      {!cars ? (
        !error && <NoddiCircularLoaderWithTrans />
      ) : cars.count === 0 ? (
        <NoddiEmptyPage title={t`No cars found`} message={texts.noCarsAssignedToYou} />
      ) : (
        <>
          {cars.results.map((car) => (
            <CarItemCard
              key={car.id}
              car={car}
              showBookingSummary={false}
              action={
                <NoddiButton size='small' endIcon='ArrowRight' onClick={() => startBooking(car)}>
                  <Trans>Book</Trans>
                </NoddiButton>
              }
            />
          ))}
          {cars.count > pagination.pageSize && (
            <NoddiBasicCard className='p-4'>
              <TablePagination
                loading={isFetching}
                pagination={pagination}
                onChange={(nextPagination) => setPagination({ ...nextPagination, search: pagination.search })}
                totalCount={cars.count}
                translations={texts.table}
              />
            </NoddiBasicCard>
          )}
        </>
      )}
    </section>
  );
};

/** Starts a booking with the car already in the cart, the way the fleet page's Create booking does. */
const useStartBookingForCar = () => {
  const startNewBooking = useStartNewBooking();

  return (car: CarFleetViewRecord) =>
    startNewBooking({
      cars: [
        {
          make: car.make,
          model: car.model,
          hasTireHotel: car.wheelStorage.length > 0,
          image: car.image,
          makeLogo: car.makeLogo,
          displayName: car.displayName,
          licensePlate: car.licensePlate
        }
      ]
    });
};
